import { locales, type I18nKey } from './locales';
import {
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  LOCALE_STORAGE_KEY,
  isLocaleId,
  type Language,
  type LocaleId,
  type TranslateParams,
} from './types';

const LEGACY_LANGUAGE_MAP: Record<string, LocaleId> = {
  zh: 'zh_CN',
  en: 'en_US',
  jp: 'ja_JP',
};

function readStorage(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage may be unavailable in private mode or restricted browser contexts.
  }
}

function removeStorage(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore restricted storage contexts.
  }
}

function resolveInitialLocale(): LocaleId {
  const current = readStorage(LOCALE_STORAGE_KEY);
  if (isLocaleId(current)) {
    removeStorage('language');
    return current;
  }

  const legacyLanguage = readStorage('language');
  const migratedLanguage = legacyLanguage && LEGACY_LANGUAGE_MAP[legacyLanguage];
  if (migratedLanguage) {
    writeStorage(LOCALE_STORAGE_KEY, migratedLanguage);
    removeStorage('language');
    return migratedLanguage;
  }

  return DEFAULT_LOCALE;
}

export class I18nService {
  private static instance: I18nService;
  private currentLanguage: LocaleId;
  private readonly listeners = new Set<() => void>();

  private constructor() {
    this.currentLanguage = resolveInitialLocale();
  }

  static getInstance(): I18nService {
    if (!I18nService.instance) {
      I18nService.instance = new I18nService();
    }
    return I18nService.instance;
  }

  getLanguage(): Language {
    return this.currentLanguage;
  }

  setLanguage(lang: Language): void {
    if (!isLocaleId(lang) || lang === this.currentLanguage) return;
    this.currentLanguage = lang;
    writeStorage(LOCALE_STORAGE_KEY, lang);
    this.notifyListeners();
  }

  t(key: I18nKey, params?: TranslateParams): string {
    let text = locales[this.currentLanguage][key]
      ?? locales[FALLBACK_LOCALE][key]
      ?? key;

    if (params) {
      Object.keys(params).forEach((name) => {
        text = text.split(`{${name}}`).join(String(params[name]));
      });
    }

    return text;
  }

  getSnapshot = (): Language => this.currentLanguage;

  getServerSnapshot = (): Language => DEFAULT_LOCALE;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  addListener(listener: () => void): void {
    this.listeners.add(listener);
  }

  removeListener(listener: () => void): void {
    this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }
}

export const i18nService = I18nService.getInstance();

export const t = (key: I18nKey, params?: TranslateParams): string =>
  i18nService.t(key, params);

export type { I18nKey, Language, LocaleId, TranslateParams };
